import React, { useRef, useCallback, useReducer } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import Text from '@/components/Typography/Text';
import FloatingLabelInput from '@/components/Inputs/FloatingLabelInput';
import { APP_NAME, API_HOST } from '@/constants/config';
import { useTranslation } from '@/hooks/useTranslation';
import Turnstile from '@/components/Turnstile';
import SubmitButton from '@/components/Buttons/SubmitButton';
import AuthPage from '@/components/Pages/AuthPage';
import PropTypes from 'prop-types';
import Link from '@/components/Typography/Link';
import MetaHead from '@/components/MetaHead';
import StatusContainer from '@/components/StatusContainer';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Platform check
const isWeb = Platform.OS === 'web';

// Create styles outside component
const styles = StyleSheet.create({
  inputContainer: {
    gap: 8,
  },
  footer: {
    gap: 12,
    marginTop: 24,
    alignItems: 'center',
  },
  footerLinks: {
    flexDirection: 'row',
    gap: 4,
    justifyContent: 'center',
  },
});

// Form reducer
const formReducer = (state, action) => {
  switch (action.type) {
    case 'SET_EMAIL':
      return { ...state, email: action.payload, error: '', success: '' };
    case 'SET_ERROR':
      return { ...state, error: action.payload, success: '' };
    case 'SET_SUCCESS':
      return { ...state, success: action.payload, error: '', email: '' };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'RESET':
      return { ...initialState };
    default:
      return state;
  }
};

// Initial state
const initialState = {
  email: '',
  error: '',
  success: '',
  isLoading: false,
};

// Memoized footer component
const Footer = React.memo(({ styles, t }) => (
  <View style={styles.footer}>
    <View style={styles.footerLinks}>
      <Text color='secondary'>{t('auth.forgotPassword.rememberPassword')}</Text>
      <Link href="/login">
        {t('auth.forgotPassword.backToLogin')}
      </Link>
    </View>
  </View>
));

Footer.propTypes = {
  styles: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

function ForgotPasswordRoute() {
  const { t } = useTranslation();
  const router = useRouter();
  const turnstileRef = useRef(null);
  
  const [formState, dispatch] = useReducer(formReducer, initialState);

  // Memoize handlers
  const handleEmailChange = useCallback((text) => {
    dispatch({ type: 'SET_EMAIL', payload: text });
  }, []);

  const handleLogoPress = useCallback(() => {
    router.push('/');
  }, [router]);

  // Form validation
  const validateEmail = (email, t) => {
    if (!email.trim()) {
      return t('auth.forgotPassword.errors.emailRequired');
    }
    if (!EMAIL_REGEX.test(email.trim())) {
      return t('auth.forgotPassword.errors.invalidEmail');
    }
    return null;
  };

  // Validate form
  const validateForm = useCallback(() => {
    const emailError = validateEmail(formState.email, t);
    if (emailError) {
      dispatch({ type: 'SET_ERROR', payload: emailError });
      return false;
    }
    return true;
  }, [formState.email, t]);

  const handleResetPassword = useCallback(async () => {
    try {
      if (!validateForm()) return;

      dispatch({ type: 'SET_LOADING', payload: true });

      // Get turnstile token on submit
      let turnstileToken = null;
      if (isWeb) {
        try {
          turnstileToken = await turnstileRef.current?.execute();
        } catch (captchaError) {
          dispatch({ type: 'SET_ERROR', payload: t('auth.forgotPassword.errors.captchaRequired') });
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }
      }

      const response = await fetch(`${API_HOST}/json/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: formState.email.trim(),
          ...(isWeb && { turnstileToken })
        }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.message || t('auth.forgotPassword.errors.resetFailed'));
      }

      dispatch({ type: 'SET_SUCCESS', payload: data.message || t('auth.forgotPassword.success') });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message || t('auth.forgotPassword.errors.resetFailed') });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [formState.email, t, validateForm]);

  return (
    <AuthPage
      title={t('auth.forgotPassword.title')}
      subtitle={t('auth.forgotPassword.subtitle')}
      onLogoPress={handleLogoPress}
    >
      <MetaHead 
        title="Forgot Password"
        description="Reset your Gab AI account password securely. We'll help you regain access to your account through our simple password recovery process."
        noindex
      />
      <View style={styles.inputContainer}>
        <FloatingLabelInput
          label={t('auth.forgotPassword.emailLabel')}
          value={formState.email}
          onChangeText={handleEmailChange}
          keyboardType="email-address"
          autoCapitalize="none"
          returnKeyType="send"
          autoComplete="email"
          textContentType="emailAddress"
          onSubmitEditing={handleResetPassword}
        />

        {formState.error ? (
          <StatusContainer type="error" message={formState.error} />
        ) : null}

        {formState.success ? (
          <StatusContainer type="success" message={formState.success} />
        ) : null}

        {isWeb && <Turnstile ref={turnstileRef} />}

        <SubmitButton
          onPress={handleResetPassword}
          isLoading={formState.isLoading}
          isDisabled={!formState.email.trim()}
          title={t('auth.forgotPassword.submitButton')}
        />

        <Footer styles={styles} t={t} />
      </View>
    </AuthPage>
  );
};

export default React.memo(ForgotPasswordRoute);